import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoLabel } from '../../Badges/InfoLabel';
import Card from '../../Card/Card';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import RowActionsMenu from '../../Fundamentals/RowActionsMenu/RowActionsMenu';
import { VossProductGroupType, VossProductGroupTypeText } from '../../../Constants/VOSS/vossProductGroupType';
import { VossProductType, VossProductTypeText } from '../../../Constants/VOSS/vossProductType';
import { VossRevenueRecognitionBehaviourText } from '../../../Constants/VOSS/vossRevenueRecognitionBehaviour';
import { VossTierChangeOperationText } from '../../../Constants/VOSS/vossTierChangeOperation';
import { VossTierTagTypeDescription, VossTierTagTypeText } from '../../../Constants/VOSS/vossTierTagType';
import { distinctTagValues, sortProductGroups, tierProductCount } from './tierUtils';
import type { RowAction } from '../../Fundamentals/RowActionsMenu/RowActionsMenu';
import type { PackageProductGroup, PackageTier } from './Packages.types';

const TYPE_LABEL: Record<VossProductGroupType, 'success' | 'info'> = {
    [VossProductGroupType.Included]: 'success',
    [VossProductGroupType.Optional]: 'info',
};

export const ProductGroupTypeLabel = ({ type }: { type: VossProductGroupType }) => {
    const { t } = useTranslation();

    return <InfoLabel type={TYPE_LABEL[type]} title={<span className="font-size-xs">{t(VossProductGroupTypeText[type])}</span>} />;
};

const PRODUCT_DOT_CLASS: Record<number, string> = {
    [VossProductType.Main]: 'text-success',
    [VossProductType.Addon]: 'text-primary',
    [VossProductType.License]: 'text-secondary',
    [VossProductType.Usage]: 'text-warning',
};

interface ProductGroupRowProps {
    group: PackageProductGroup;
    isExpanded: boolean;
    onToggle: () => void;
}

const ProductGroupRow = ({ group, isExpanded, onToggle }: ProductGroupRowProps) => {
    const { t } = useTranslation();
    const contentId = useId();
    const products = group.products ?? [];

    return (
        <div className="border rounded-8">
            <button
                type="button"
                className="bg-transparent border-0 w-100 cursorpointer d-flex align-items-center flex-wrap gap-8 px-12 py-8 text-left"
                aria-expanded={isExpanded}
                aria-controls={contentId}
                onClick={onToggle}
            >
                <SpirisIcon name={isExpanded ? 'chevron-down' : 'chevron-right'} size="sm" className="icon-neutral" />
                <SpirisIcon name="purchasing" size="sm" className="icon-neutral" />
                <span className="font-weight-semibold">{group.name}</span>
                {(group.externalId ?? '') !== '' && <code className="text-secondary font-weight-normal font-size-xs">{group.externalId}</code>}
                {group.productGroupType !== undefined && <ProductGroupTypeLabel type={group.productGroupType} />}
                {group.revenueRecognitionBehaviour !== undefined && (
                    <InfoLabel title={<span className="font-weight-normal font-size-xs">{t(VossRevenueRecognitionBehaviourText[group.revenueRecognitionBehaviour])}</span>} />
                )}
                {(group.tags ?? []).map((tag, index) => (
                    <InfoLabel key={index} title={<code className="font-weight-normal font-size-xs">{tag.tag}</code>} />
                ))}
                <span className="ml-auto text-secondary font-13">{t('packages.productCount', { count: products.length })}</span>
            </button>
            {isExpanded && (
                <div id={contentId}>
                    {products.map((product) => (
                        <div key={product.id} className="d-flex justify-content-between align-items-center gap-16 border-top px-12 py-8">
                            <span className="d-flex align-items-center gap-8">
                                <span className={`${PRODUCT_DOT_CLASS[product.type ?? VossProductType.Main]} font-size-xs`} aria-hidden="true">
                                    ●
                                </span>
                                {product.name}
                            </span>
                            <span className="d-flex align-items-center gap-8 text-secondary">
                                {product.type !== undefined && t(VossProductTypeText[product.type])}
                                <code className="font-weight-normal font-size-xs">{product.articleNumber}</code>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

interface PackageTierCardProps {
    tier: PackageTier;
    position: number;
    dragHandle?: ReactNode;
    onEdit?: (tier: PackageTier) => void;
    onDelete?: (tier: PackageTier) => void;
}

const PackageTierCard = ({ tier, position, dragHandle, onEdit, onDelete }: PackageTierCardProps) => {
    const { t } = useTranslation();
    const contentId = useId();
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);

    const tags = tier.tags ?? [];
    const restrictedOperations = tier.restrictedChangeOperations ?? [];
    const productGroups = sortProductGroups(tier.productGroups ?? []);
    const groupIds = productGroups.map((group) => group.id ?? '');
    const allGroupsExpanded = groupIds.length > 0 && groupIds.every((groupId) => expandedGroupIds.includes(groupId));

    const toggleGroup = (groupId: string) =>
        setExpandedGroupIds((current) => (current.includes(groupId) ? current.filter((expandedId) => expandedId !== groupId) : [...current, groupId]));

    const actions: RowAction[] = [
        ...(onEdit === undefined ? [] : [{ id: 'edit', label: t('packages.editTier'), icon: 'edit', onClick: () => onEdit(tier) }]),
        ...(onDelete === undefined ? [] : [{ id: 'delete', label: t('packages.deleteTier'), icon: 'delete', destructive: true, onClick: () => onDelete(tier) }]),
    ];

    return (
        <Card>
            <div className="d-flex align-items-center gap-12 px-16 py-12">
                {dragHandle}
                <button
                    type="button"
                    className="bg-transparent border-0 px-0 py-4 cursorpointer d-flex align-items-center gap-12 flex-1 min-w-0 text-left"
                    aria-expanded={isExpanded}
                    aria-controls={contentId}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <span className="badge flex-shrink-0">{position}</span>
                    <SpirisIcon name={isExpanded ? 'chevron-down' : 'chevron-right'} size="sm" className="icon-neutral" />
                    <span className="min-w-0">
                        <span className="d-flex align-items-baseline gap-8">
                            <span className="font-weight-semibold">{tier.name}</span>
                            {(tier.externalId ?? '') !== '' && <code className="text-secondary font-weight-normal font-size-xs">{tier.externalId}</code>}
                        </span>
                        <span className="d-block text-secondary font-13">
                            {t('packages.productGroupCount', { count: productGroups.length })} · {t('packages.productCount', { count: tierProductCount(tier) })}
                        </span>
                    </span>
                </button>
                <span className="d-flex align-items-center justify-content-end flex-wrap gap-8">
                    {distinctTagValues(tags).map((value) => (
                        <InfoLabel key={value} title={<code className="font-weight-normal font-size-xs">{value}</code>} />
                    ))}
                    {restrictedOperations.length > 0 && (
                        <InfoLabel
                            type="danger"
                            title={
                                <span className="d-flex align-items-center gap-4 text-error font-size-xs">
                                    <SpirisIcon name="lock" size="sm" className="icon-error" />
                                    {t('packages.restrictionCount', { count: restrictedOperations.length })}
                                </span>
                            }
                        />
                    )}
                </span>
                {actions.length > 0 && <RowActionsMenu label={t('packages.rowActions')} actions={actions} />}
            </div>
            {isExpanded && (
                <div id={contentId} className="d-flex flex-column gap-16 border-top px-16 py-16">
                    {restrictedOperations.length > 0 && (
                        <div className="d-flex align-items-center flex-wrap gap-8">
                            <span className="d-inline-flex align-items-center gap-6 text-error font-13 font-weight-semibold">
                                <SpirisIcon name="lock" size="sm" className="icon-error" />
                                {t('packages.blockedOperations')}
                            </span>
                            {restrictedOperations.map((operation) => (
                                <InfoLabel key={operation} type="danger" title={<span className="text-error font-size-xs">{t(VossTierChangeOperationText[operation])}</span>} />
                            ))}
                        </div>
                    )}
                    {tags.length > 0 && (
                        <div>
                            <h4 className="font-size-xs text-uppercase text-secondary mb-8">{t('packages.tierTags', { total: tags.length })}</h4>
                            <div className="d-flex flex-column gap-8">
                                {tags.map((tag, index) => (
                                    <div key={index} className="border rounded-8 px-12 py-8">
                                        <div className="d-flex align-items-center flex-wrap gap-8">
                                            <code className="font-weight-normal font-size-xs">{tag.tag}</code>
                                            {tag.type !== undefined && (
                                                <InfoLabel title={<span className="font-weight-normal font-size-xs">{t(VossTierTagTypeText[tag.type])}</span>} />
                                            )}
                                            {tag.rank !== null && tag.rank !== undefined && (
                                                <span className="text-secondary font-13">{t('packages.tagRank', { rank: tag.rank })}</span>
                                            )}
                                        </div>
                                        {tag.type !== undefined && <p className="text-secondary font-13 mt-4 mb-0">{t(VossTierTagTypeDescription[tag.type])}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-8">
                            <h4 className="font-size-xs text-uppercase text-secondary mb-0">{t('packages.tierProductGroups', { total: productGroups.length })}</h4>
                            {productGroups.length > 1 && (
                                <button type="button" className="btn btn-link px-0 py-4" onClick={() => setExpandedGroupIds(allGroupsExpanded ? [] : groupIds)}>
                                    {allGroupsExpanded ? t('packages.collapseAll') : t('packages.expandAll')}
                                </button>
                            )}
                        </div>
                        {productGroups.length === 0 ? (
                            <p className="text-secondary mb-0">{t('packages.noProductGroups')}</p>
                        ) : (
                            <div className="d-flex flex-column gap-8">
                                {productGroups.map((group) => (
                                    <ProductGroupRow
                                        key={group.id}
                                        group={group}
                                        isExpanded={expandedGroupIds.includes(group.id ?? '')}
                                        onToggle={() => toggleGroup(group.id ?? '')}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};

export default PackageTierCard;
